/**
 * SemanticSnapshot — the interlingua every semantic connector translates
 * to and from. Generalizes the whiteboard photo pipeline's WhiteboardScene
 * (which stays untouched; it is the capture dialect's raw form) into the
 * normalized representation used for excursion baselines and return diffs.
 *
 * Invariants (02-DESIGN §2.2):
 *  1. Relations use only the canonical relationship taxonomy (canon 12).
 *     Dialect vocabulary is resolved by the semantic maps BEFORE a snapshot
 *     is final.
 *  2. Every inferred assertion carries confidence + basis. Explicit facts
 *     get basis 'explicit', confidence 1.
 */

import type { RelationshipEdgeType, RelationshipDirectionality } from '../../types/prioritization/itemRelationships';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';

/** Where an inferred value came from — uncertainty is first-class. */
export type AssertionBasis =
  | 'explicit'        // stated in the dialect (a caption, a typed link)
  | 'symbol'          // a written marker ("blocks", "⊃") — beats line style
  | 'visual_language' // resolved via workspace signifier / semantic map rules
  | 'spatial'         // inferred from layout (containment, proximity, lanes)
  | 'llm_inference';  // model judgment (e.g. vision transcription)

export interface Assertion<T = unknown> {
  value: T;
  /** 0–1. Explicit facts are 1. */
  confidence: number;
  basis: AssertionBasis;
}

export interface SemanticObjectContent {
  text: string;
  description?: string;
  attachments?: string[];
}

export interface SemanticObject {
  /** Native id in the dialect (Miro item id) or anchor code (photo capture). */
  externalId: string;
  /** Member of the profile's nativeObjectTypes. */
  nativeType: string;
  content: SemanticObjectContent;
  /** Raw visual cues by channel (e.g. { fill_color: 'red', shape: 'diamond' }). */
  visual: Partial<Record<string, string>>;
  /** Normalized layout, origin top-left, x/y in [0,1] of the container. */
  layout?: { x: number; y: number; regionId?: string };
  /** Mapped meaning: itemCategory, altitude, attributes — each with uncertainty. */
  asserted: {
    itemCategory?: Assertion<ItemLanguageType>;
    altitudeLevel?: Assertion<number>;
    /** Anything else the semantic map routed (metadata attributes, enums…). */
    attributes?: Record<string, Assertion>;
  };
  /** When this object corresponds to a platform item (export or matched return). */
  internalId?: string;
}

export interface SemanticRelation {
  sourceExternalId: string;
  targetExternalId: string;
  /** Canonical only (canon 12). */
  canonicalType: RelationshipEdgeType;
  subtype?: string;
  directionality: RelationshipDirectionality;
  /** The dialect cue that produced this relation, for review UI honesty. */
  cue?: string;
  assertion: Assertion<RelationshipEdgeType>;
}

/** Structure plane: frames, lanes, quadrants, sections. */
export interface SemanticRegion {
  externalId: string;
  title: string;
  kind: 'frame' | 'lane' | 'quadrant' | 'section';
  layout?: { x: number; y: number; width: number; height: number };
}

export interface SemanticAnnotation {
  externalId: string;
  text: string;
  /** Object this annotation is attached to, when known. */
  targetExternalId?: string;
}

export interface SemanticSnapshotSource {
  connectorType: string;
  /** Board / doc / space id in the dialect. */
  containerId: string;
  containerUrl?: string;
  capturedAt: string;
  /** Photos remain provenance (capture profiles). */
  captureArtifactUrls?: string[];
}

export interface SemanticSnapshot {
  source: SemanticSnapshotSource;
  objects: SemanticObject[];
  relations: SemanticRelation[];
  regions: SemanticRegion[];
  annotations: SemanticAnnotation[];
  warnings: string[];
  /** Never force-coerce what wasn't understood (whiteboard precedent). */
  unrecognized: unknown[];
}

export function emptySnapshot(source: SemanticSnapshotSource): SemanticSnapshot {
  return { source, objects: [], relations: [], regions: [], annotations: [], warnings: [], unrecognized: [] };
}

/** Explicit fact helper — confidence 1 by definition. */
export function explicitAssertion<T>(value: T): Assertion<T> {
  return { value, confidence: 1, basis: 'explicit' };
}
